/**
 * @lines98/client/screens/book-screen — страница «Книга реплеев» `/book` (тикет A,
 * frontend-lead 2026-06-24). Личный каталог реплеев: два альбома (segmented
 * [Свои|Чужие]) × вкладки режимов (segmented Стандарт·Классика·Вслепую·Доска дня).
 * Контент среза (альбом × режим) — компактный список-строка (БЕЗ миниатюр доски,
 * §1.2). Клик по строке → существующий плеер общим replay-flow (gate §3A). Сильный
 * empty-state на каждом срезе. account-bound: гость → CTA логина.
 *
 * Модель «ствол → ветки» (CLAUDE.md / code-structure §1): экран = ТОНКАЯ ветка,
 * монтирующая ОБЩИЕ кирпичи —
 *   · components/segmented   — переключатель альбома + вкладки режимов (НЕ кустарный <button>);
 *   · lib/mode-registry      — метки/иконки/порядок режимов (НЕ хардкод «Стандарт/…»);
 *   · styles/leaderboard.css — визуальный язык строк (rows-scroll bounded inner-scroll);
 *   · lib/screen-snapshot    — instant-hydration (мгновенный кадр из снимка, не «Загружаем…»);
 *   · lib/format             — числа/относительные даты (единый источник);
 *   · components/info-popover — descriptive «честная рамка» за `?` на touch (§0.E);
 *   · services/book-api      — серверная дорожка (ADR-0023 §API), keyset-пагинация;
 *   · общий replay-flow      — открытие партии (showReplayById → app.ts GET /api/replay/:id).
 *
 * Числа НЕ хардкодятся: ёмкость «Чужих» (Y) приходит с сервера (GET /book/capacity →
 * others.{used,capacity}); у «Своих» капа нет (AC-A17/AC-A2). Honest-framing (§3.3):
 * никаких «иначе реплей пропадёт»/FOMO; счётчик «Чужих» = про ёмкость подборки.
 *
 * Layout — co-located styles/book.css (layout-plan в шапке CSS, §5.0). Демо/viewport-
 * тесты МОКАЮТ HTTP-эндпоинты book-api → экран self-fetch'ит боевым путём (instant-
 * hydration + SWR); `?demo=book&album=` задаёт лишь стартовый альбом.
 *
 * Cross-references: тикет A AC-A1..A17; спека replay-collection.md §5; ADR-0023 §API-
 *   контракт; образец leaderboard-screen.ts (ближайший аналог — data-list + табы).
 */

import './../styles/book.css';

import { confirmDialog } from '../components/confirm-dialog.js';
import { ICON_SAVE_OFF } from '../components/icon-button.js';
import { createSegmented, type SegmentedHandle } from '../components/segmented.js';
import { createText } from '../components/text.js';
import { escapeHtml, formatRuNumber } from '../lib/format.js';
import { formatDateTime } from '../lib/format-datetime.js';
import { t } from '../lib/i18n/index.js';
import { MODE_REGISTRY, modeLabel, type ModeEntry } from '../lib/mode-registry.js';
import { createScreenSnapshot } from '../lib/screen-snapshot.js';
import { installSurfaceReflow } from '../lib/surface-reflow.js';
import {
  getBookCapacity,
  getOthersAlbum,
  getOwnAlbum,
  removeOthersBookmark,
  type BookAlbum,
  type BookCapacity,
  type BookRow,
  type BookTab,
} from '../services/book-api.js';
import { useGameStore } from '../store/game-store.js';
import type { ContentLang } from '@lines98/shared/config/seo';
import { REPLAY_BOOK_OWN_GUEST_CAP_PER_MODE } from '@lines98/shared/config/balance';
import type { ScreenHandle } from './board-screen.js';

// =============================================================================
// Mount options. `?demo=book` viewport-тесты МОКАЮТ HTTP-эндпоинты (book-api),
// поэтому экран РЕАЛЬНО self-fetch'ит (instant-hydration + SWR — всё боевое);
// демо лишь задаёт стартовый альбом через URL (`&album=`). empty/orphan/guest
// приходят из мок-ОТВЕТОВ сервера (пустые массивы / orphan-строка / 401), как боевой путь.
// =============================================================================

export interface BookScreenOptions {
  /** Стартовый альбом (own|others) — для deep-link / demo (`?demo=book&album=`). */
  readonly initialAlbum?: BookAlbum;
}

// =============================================================================
// Вкладки режимов — из реестра режимов (единый источник, НЕ хардкод-список).
// 4 вкладки: 3 casual-режима (predict/classic/blind) + «Доска дня» (is_daily).
// =============================================================================

/** Стабильный id вкладки «Доска дня» (отдельная ось от режимов — §2.4 спеки). */
const DAILY_TAB_ID: BookTab = 'daily';

/** Записи casual-режимов (играбельные, в порядке реестра) — predict/classic/blind. */
function casualModeEntries(): ModeEntry[] {
  return MODE_REGISTRY.filter((e) => e.family === 'casual' && !e.locked && e.mode);
}

/** id вкладки → серверный `?mode=` (predict|classic|blind|daily). */
type TabId = BookTab;

// =============================================================================
// Snapshot (instant-hydration §3) — последний показанный срез на ключ (альбом×вкладка).
// ВЕТКА общей фабрики screen-snapshot (НЕ свой кэш-движок). Per-player, версионируется.
// =============================================================================

interface BookSnapshotSlice {
  readonly rows: ReadonlyArray<BookRow>;
}
interface BookSnapshotPayload {
  /** ключ `${album}:${tab}` → последняя показанная страница среза. */
  readonly slices: Record<string, BookSnapshotSlice>;
  /** последние server-truth счётчики (для мгновенного counter). */
  readonly capacity: BookCapacity | null;
}

const bookSnapshot = createScreenSnapshot<BookSnapshotPayload>({
  keyPrefix: 'lines98.book.snapshot.v1.',
  version: 1,
  isValid: (v): v is BookSnapshotPayload =>
    typeof v === 'object' && v !== null && 'slices' in v,
});

// =============================================================================
// Mount
// =============================================================================

export function mountBookScreen(host: HTMLElement, opts: BookScreenOptions = {}): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'book-screen screen-root';
  host.appendChild(root);

  let destroyed = false;
  let activeAlbum: BookAlbum = opts.initialAlbum ?? 'own';
  let activeTab: TabId = casualModeEntries()[0]?.mode ?? 'predict';
  let guest = false;
  // server-truth (owner-directive 2026-06-25): игрок НЕ зарегистрирован (registered=false).
  // ПОДПИСЬ «Свои» и текст «Чужих» завязаны на ЭТО (показ ЛЮБОМУ гостю, даже при ≤3 играх),
  // а не на усечение архива. Приходит с /book/own (ListResult.isGuest).
  let serverIsGuest = false;

  // server-truth счётчики + страницы срезов (снимок мгновенно + SWR-ревалидация).
  let capacity: BookCapacity | null = null;
  const slices = new Map<string, ReadonlyArray<BookRow>>();
  const sliceKey = (album: BookAlbum, tab: TabId): string => `${album}:${tab}`;

  // --- Instant-hydration (§3): синхронный снимок последнего показанного среза/счётчиков. ---
  const snap = bookSnapshot.load();
  if (snap) {
    capacity = snap.capacity;
    for (const [k, s] of Object.entries(snap.slices)) slices.set(k, s.rows);
  }

  // ---------------------------------------------------------------------------
  // Stable shell — кирпичи монтируются ОДИН раз (как лидерборд).
  // ---------------------------------------------------------------------------
  const header = document.createElement('header');
  header.className = 'book-header';

  const titleRow = document.createElement('div');
  titleRow.className = 'book-title-row';
  const titleEl = document.createElement('h2');
  titleEl.className = 'book-title screen-title';
  titleEl.textContent = t('book.title');
  titleRow.appendChild(titleEl);

  // Пояснительный текст «честной рамки» + кнопка `?` УБРАНЫ (owner-directive 2026-06-25):
  // на странице остаётся только заголовок. (`book.framing` больше не показывается.)

  header.append(titleRow);

  // Переключатель альбома [Свои|Чужие] (segmented, AC-A2).
  const albumSwitch: SegmentedHandle = createSegmented({
    items: [
      { id: 'own', label: t('book.album.own') },
      { id: 'others', label: t('book.album.others') },
    ],
    activeId: activeAlbum,
    ariaLabel: t('book.album.own') + ' / ' + t('book.album.others'),
    className: 'book-album-switch',
    onChange: (id) => switchAlbum(id as BookAlbum),
  });

  // Контекстный счётчик «N партий» / «Занято X из Y» УБРАН (owner-directive 2026-06-25).

  // Вкладки режимов (segmented, метки из mode-registry — НЕ хардкод). lang = глобальный.
  const lang = useGameStore.getState().locale;
  const modeTabs: SegmentedHandle = createSegmented({
    items: [
      ...casualModeEntries().map((e) => ({ id: e.mode as string, label: modeLabel(e, lang) })),
      { id: DAILY_TAB_ID, label: t('book.tab.daily') },
    ],
    activeId: activeTab,
    ariaLabel: t('book.title'),
    className: 'book-mode-tabs',
    onChange: (id) => switchTab(id as TabId),
  });

  // Тело списка — bounded inner-scroll. data-table-scroll — ОБЩИЙ хром таблицы
  // (components/data-table): контейнер-панель + scroll. book-rows-scroll держит своё (grid-area).
  const rowsScroll = document.createElement('div');
  rowsScroll.className = 'book-rows-scroll data-table-scroll';
  rowsScroll.setAttribute('role', 'region');
  rowsScroll.setAttribute('aria-label', t('book.title'));

  // CTA «Расширить ёмкость» — ТОЛЬКО «Чужие» (findable, без цен; AC-A10). Скрыт на «Своих».
  const expandRow = document.createElement('div');
  expandRow.className = 'book-expand-row';
  const expandBtn = document.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'book-expand btn';
  expandBtn.textContent = `${t('book.expand')} · ${t('book.expandSoon')}`;
  expandBtn.setAttribute('aria-label', t('book.expandAria'));
  // v1 — без экрана покупки (пострелиз); кнопка findable, действие-заглушка (не блокирует Книгу).
  expandBtn.disabled = true;
  expandRow.appendChild(expandBtn);

  // Upsell-объяснение капа гостя (owner G-2): ОБЪЯСНЯЕТ «почему всего 3 игры видно» +
  // конверсия (войти за полным архивом). Через примитив components/text (роль body, тон
  // soft) — §2.1 НЕ bespoke: размер из роли-токена, текст fluid/не обрезается (§4.3), а не
  // кустарный inline-div. Появляется ТОЛЬКО в «Свои» гостю с усечённым архивом; вставляется
  // в КОНЕЦ списка (под тройкой строк) — внутри bounded inner-scroll, БЕЗ изменения грид-
  // строк (нулевая коллизия с параллельной правкой раскладки book.css). aria-live: polite —
  // SWR-подмена с снимка на server-truth озвучивается ассистивным технологиям.
  const guestCapText = createText({
    role: 'body',
    tone: 'soft',
    as: 'p',
    text: t('book.guestOwnCap'),
    className: 'book-guest-cap',
  });
  guestCapText.element.setAttribute('aria-live', 'polite');

  root.append(header, albumSwitch.element, modeTabs.element, rowsScroll, expandRow);

  // Math-триггер «навигация вбок» (owner-decision 2026-06-25, как у лидерборда): на
  // коротком ландшафте, где в стеке влезает < 7 строк, обвязка (заголовок/альбом/счётчик/
  // вкладки/расширить) уезжает в ПРАВУЮ колонку, список — на всю высоту слева (общий
  // lib/surface-reflow + grid-remap в book.css). 5 nav-блоков; listOverheadM=1 (у списка
  // нет строки-заголовка колонок, в отличие от таблицы лидерборда). Ставит data-reflow
  // на root, держит актуальным при ресайзе/повороте.
  const reflowTeardown = installSurfaceReflow(root, { navRows: 4, listOverheadM: 4 });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const refreshChrome = (): void => {
    root.dataset.album = activeAlbum;
    // Точка расширения — ТОЛЬКО «Чужие» (AC-A10). В «Своих» её НЕТ В DOM (не просто hidden:
    // тест считает `.book-expand` toHaveCount(0) — скрытый элемент тоже считается). Поэтому
    // физически добавляем/убираем expandRow из дерева по альбому.
    if (activeAlbum === 'others') {
      if (!expandRow.isConnected) root.appendChild(expandRow);
    } else if (expandRow.isConnected) {
      expandRow.remove();
    }
  };

  // Подпись гостя: показываем в «Свои» ЛЮБОМУ гостю (serverIsGuest, не только усечённому)
  // И когда есть ≥1 строка (под реплеями; owner-directive 2026-06-25). innerHTML списка
  // пересобирается на каждый renderBody → примитив отцепляется; переприсоединяем/убираем
  // в КОНЕЦ rowsScroll по условию.
  const refreshGuestCap = (): void => {
    const ownRows = slices.get(sliceKey('own', activeTab));
    const show = activeAlbum === 'own' && serverIsGuest && (ownRows?.length ?? 0) > 0;
    if (show) {
      rowsScroll.appendChild(guestCapText.element); // в конец списка, под строками
    } else if (guestCapText.element.isConnected) {
      guestCapText.element.remove();
    }
  };

  // «Гость» = НЕ зарегистрирован (не может сохранять чужие записи): либо 401 без
  // идентичности (`guest`), либо server-truth registered=false (`serverIsGuest`, /book/own).
  // Прокси для текста «Чужих» + подписи «Свои» (owner-directive 2026-06-25).
  const isGuestUser = (): boolean => guest || serverIsGuest;

  const renderBody = (): void => {
    if (destroyed) return;
    if (guest) {
      // 401 (нет идентичности) — БЕЗ стены «Войди, чтобы открыть Книгу» (owner-directive
      // 2026-06-25: не плодить тексты, не противоречить «гость видит 3 реплея»). Гость
      // видит ОБЫЧНЫЙ empty-state альбома, как любой под-вид гостя: «Свои» → играть;
      // «Чужие» → «Войдите, чтобы сохранять чужие записи в архив.».
      rowsScroll.innerHTML = renderEmpty(activeAlbum, activeTab, lang, true);
      wireEmptyCta();
      wireGuestCta();
      refreshGuestCap();
      return;
    }
    const rows = slices.get(sliceKey(activeAlbum, activeTab));
    if (rows === undefined) {
      rowsScroll.innerHTML = renderSkeleton(activeAlbum);
      refreshGuestCap();
      return;
    }
    if (rows.length === 0) {
      rowsScroll.innerHTML = renderEmpty(activeAlbum, activeTab, lang, isGuestUser());
      wireEmptyCta();
      wireGuestCta(); // гостевой «Чужие»-empty несёт .book-login-cta
      refreshGuestCap();
      return;
    }
    let rowsHtml = rows.map((r) => renderRow(r, activeAlbum, lang)).join('');
    // Гость на «Свои»: добиваем пустыми строками до cap (3), чтобы подпись стояла
    // «под 3 реплеями» даже при 1-2 записях (owner-directive 2026-06-25: «надпись на
    // своём месте, как будто под 3 реплеями»). Любой гость (serverIsGuest), не только усечённый.
    if (
      activeAlbum === 'own' &&
      serverIsGuest &&
      rows.length < REPLAY_BOOK_OWN_GUEST_CAP_PER_MODE
    ) {
      const pad = REPLAY_BOOK_OWN_GUEST_CAP_PER_MODE - rows.length;
      rowsHtml += '<div class="book-row book-row--placeholder data-table-row" aria-hidden="true"></div>'.repeat(pad);
    }
    rowsScroll.innerHTML = renderTable(rowsHtml, activeAlbum);
    wireRows();
    refreshGuestCap();
  };

  // ---------------------------------------------------------------------------
  // Empty / guest CTA wiring
  // ---------------------------------------------------------------------------
  const wireEmptyCta = (): void => {
    rowsScroll.querySelector<HTMLButtonElement>('.book-empty-cta')?.addEventListener('click', () => {
      if (activeAlbum === 'others') {
        useGameStore.getState().showLeaderboard();
        return;
      }
      // «Свои»: пустая вкладка → играть этот режим (daily → к доске дня).
      if (activeTab === DAILY_TAB_ID) useGameStore.getState().showDaily();
      else useGameStore.getState().showModeSelect();
    });
  };

  const wireGuestCta = (): void => {
    rowsScroll.querySelector<HTMLButtonElement>('.book-login-cta')?.addEventListener('click', () => {
      // Общий auth-flow живёт в app-shell; здесь — store-направление к логину через nav.
      // Гостевой клик ведёт в лобби, где пилюля «Войти» (единый auth-flow) на рейле.
      window.dispatchEvent(new CustomEvent('lines98:request-login'));
    });
  };

  // ---------------------------------------------------------------------------
  // Row interaction — клик по строке → плеер (общий replay-flow, gate §3A); клик по
  // кнопке удаления → убрать закладку (только «Чужие»). Действия не путаются (§8.7).
  // ---------------------------------------------------------------------------
  const wireRows = (): void => {
    rowsScroll.querySelectorAll<HTMLElement>('.book-row').forEach((row) => {
      const shortId = row.dataset.shortId;
      const available = row.dataset.available !== 'false';
      const removeBtn = row.querySelector<HTMLButtonElement>('.book-row-remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (shortId) void doRemove(shortId);
        });
      }
      // Открытие — общий replay-flow (showReplayById → app.ts GET /api/replay/:id, gate §3A).
      // Осиротевшая строка тоже кликабельна → плеер вернёт 404 → graceful «недоступна».
      const open = (): void => {
        if (shortId && available) useGameStore.getState().showReplayById(shortId);
        else if (shortId) useGameStore.getState().showReplayById(shortId); // осиротевшая → 404 fallback
      };
      row.addEventListener('click', open);
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      });
    });
  };

  async function doRemove(shortId: string): Promise<void> {
    // Подтверждение перед снятием (owner-directive 2026-06-25): удаление = выбор, поэтому
    // ДИАЛОГ Да/Нет (общий примитив confirm-dialog — native <dialog>, focus-trap, Esc), а
    // НЕ тост. Отмена/Esc/клик по фону → не удаляем. Тексты i18n (book.removeConfirm.*,
    // 15 языков): «Убрать запись из архива?» + «Убрать»/«Отмена».
    const confirmed = await confirmDialog({
      title: t('book.removeConfirm.title'),
      confirmLabel: t('book.removeConfirm.confirm'),
      cancelLabel: t('book.removeConfirm.cancel'),
    });
    if (destroyed || !confirmed) return;
    const res = await removeOthersBookmark(shortId);
    if (destroyed || !res.ok) return;
    // Освободить слот локально (оптимистично) + ревалидация счётчика.
    const key = sliceKey('others', activeTab);
    const rows = slices.get(key) ?? [];
    slices.set(key, rows.filter((r) => r.short_id !== shortId));
    if (capacity) {
      capacity = {
        own: capacity.own,
        others: { used: Math.max(0, capacity.others.used - 1), capacity: capacity.others.capacity },
      };
    }
    refreshChrome();
    renderBody();
    void revalidate();
  }

  // ---------------------------------------------------------------------------
  // Switch
  // ---------------------------------------------------------------------------
  const switchAlbum = (album: BookAlbum): void => {
    if (album === activeAlbum) return;
    activeAlbum = album;
    refreshChrome();
    renderBody();
    void loadSlice(activeAlbum, activeTab);
  };

  const switchTab = (tab: TabId): void => {
    if (tab === activeTab) return;
    activeTab = tab;
    refreshChrome();
    renderBody();
    void loadSlice(activeAlbum, activeTab);
  };

  // ---------------------------------------------------------------------------
  // Fetch (keyset-пагинация first page; bounded-scroll + cursor-пролистывание под архив)
  // ---------------------------------------------------------------------------
  const loadSlice = async (album: BookAlbum, tab: TabId): Promise<void> => {
    if (guest || destroyed) return;
    const res = album === 'own'
      ? await getOwnAlbum({ mode: tab })
      : await getOthersAlbum({ mode: tab });
    if (destroyed) return;
    if (!res.ok) {
      if (res.reason === 'unauthorized') {
        guest = true;
        renderBody();
      }
      return;
    }
    slices.set(sliceKey(album, tab), res.items);
    // guest_capped — server-truth ТОЛЬКО «Свои» (G-2). Запоминаем флаг последнего среза
    // «Свои»; «Чужие» его не несут (res.guestCapped там всегда false). На зарегистрированном
    // сервер тоже шлёт false → upsell не покажется. Снимок не хранит флаг (он дешёвый и
    // приходит первым же server-truth ответом; снимок — только мгновенный кадр строк).
    // server-truth «не зарегистрирован» (ТОЛЬКО «Свои») → подпись/Чужие-текст.
    if (album === 'own') serverIsGuest = res.isGuest;
    if (album === activeAlbum && tab === activeTab) renderBody();
    saveSnapshot();
  };

  const revalidate = async (): Promise<void> => {
    if (guest || destroyed) return;
    const cap = await getBookCapacity();
    if (destroyed) return;
    if (cap.ok) {
      capacity = cap.capacity;
      saveSnapshot();
    } else if (cap.reason === 'unauthorized') {
      guest = true;
      renderBody();
    }
  };

  const saveSnapshot = (): void => {
    const sliceObj: Record<string, BookSnapshotSlice> = {};
    for (const [k, rows] of slices) sliceObj[k] = { rows };
    bookSnapshot.save({ slices: sliceObj, capacity });
  };

  // ---------------------------------------------------------------------------
  // Initial paint + boot fetch (SWR — снимок мгновенно, сеть ревалидирует).
  // ---------------------------------------------------------------------------
  refreshChrome();
  renderBody();

  // Вкладки режимов на узком touch-landscape — ВИДИМЫЙ список (ужатый сеткой 2×2 в
  // book.css §SIDE-RAIL), БЕЗ ☰-drawer (CANON-3, owner 2026-06-24: drawer сломан → вырезан,
  // §10.4). Прежний lib/book-nav-fit (измеряемая деградация в ☰-dropdown) удалён целиком.

  // Боевой путь: экран self-fetch'ит server-truth (мокается в viewport-тестах). 401 →
  // guest (CTA логина). Снимок (если был) уже отрисован → SWR тихо подменит на server-truth.
  void revalidate();
  void loadSlice(activeAlbum, activeTab);

  return {
    unmount(): void {
      destroyed = true;
      reflowTeardown();
      albumSwitch.destroy();
      modeTabs.destroy();
      host.innerHTML = '';
    },
  };
}

// =============================================================================
// Render helpers (pure HTML — визуальный язык leaderboard.css + book.css)
// =============================================================================

/** SVG-разметка перечёркнутой дискеты для inline-строки (тот же глиф-реестр, ICON_SAVE_OFF). */
/** Обёртка-таблица (хром примитива data-table): строка-заголовок «Счёт·Ходы·Дата[·действие]»
 * + строки. Колонки (.book-table grid-template-columns) — в book.css (по `[data-album]`:
 * «Чужие» получают 4-ю КОЛОНКУ-действие справа от «Дата», как значок-просмотра-реплея в
 * лидерборде вынесен в отдельную правую зону). Точки-маркеры режима убраны (owner-directive
 * 2026-06-25); режим виден по активной вкладке. */
function renderTable(rowsHtml: string, album: BookAlbum): string {
  // Подписи колонок ПЕРЕИСПОЛЬЗУЮТ классы ячеек данных — как лидерборд (его «Счёт» в шапке
  // = .leaderboard-row-score → accent, «Ходы» = -moves → ink-soft). Так цвет/вес подписи
  // точь-в-точь как у значений: «СЧЁТ» акцентный, «ХОДЫ» тише (owner-directive 2026-06-25,
  // «цвет Счёт должен совпадать с лидербордом»). data-table-header добавляет uppercase/
  // opacity/letter-spacing поверх. Подписи — i18n: book.col.score/moves/date
  // (RU «Счёт»/«Ходы»/«Дата»; score≠figures.score=«Очки» — owner-rename Очки→Счёт).
  // «Чужие»: 4-я ПУСТАЯ header-ячейка `.book-row-action` держит колонку действия выровненной
  // (зеркало пустого `<span class="leaderboard-row-icons">` в шапке лидерборда) → подпись
  // «ДАТА» стоит ровно над датами, НЕ над кнопкой удаления (owner-directive 2026-06-25).
  const actionHeader = album === 'others' ? `<span class="book-row-action"></span>` : '';
  return `<div class="book-table data-table">
    <div class="book-header-row data-table-header" aria-hidden="true">
      <span class="book-row-score">${escapeHtml(t('book.col.score'))}</span>
      <span class="book-row-moves">${escapeHtml(t('book.col.moves'))}</span>
      <span class="book-col--date">${escapeHtml(t('book.col.date'))}</span>
      ${actionHeader}
    </div>
    ${rowsHtml}
  </div>`;
}

/** Кнопка-удаления строки «Чужих» — icon-button (framed, ≥44×44 из примитива) с глифом
 * ПЕРЕЧЁРКНУТОЙ дискеты (ICON_SAVE_OFF, единый реестр). Семантика «убрать из архива»
 * (un-save), стиль = framed icon-button значка-просмотра лидерборда. */
function removeButtonHtml(): string {
  return `<button type="button" class="book-row-remove icon-btn icon-btn--framed" aria-label="${escapeHtml(t('book.row.remove'))}">${ICON_SAVE_OFF}</button>`;
}

function renderRow(row: BookRow, album: BookAlbum, lang: ContentLang): string {
  // Строка ОТОБРАЖАЕТ ТОЧНУЮ дату+время сыгранной партии (`finished_at`) до секунд
  // (owner перекрыл относительную дату; единый форматтер lib/format-datetime, AC-6).
  // У ОБОИХ альбомов — finished_at: у «Чужих» поле saved_at используется для СОРТИРОВКИ
  // среза (серверная дорожка book-api), но в строке стоит дата ИГРЫ, не дата закладки.
  const dateLabel = formatDateTime(row.finished_at, lang);
  const available = row.available !== false;
  // Действие — в ОТДЕЛЬНОЙ правой колонке `.book-row-action` (НЕ внутри `.book-row-end`),
  // как значок-просмотра в лидерборде вынесен в свою зону (owner-directive 2026-06-25):
  // дата перестаёт поджиматься кнопкой, столбец «Дата» и его заголовок выровнены.
  const actionCell = album === 'others'
    ? `<span class="book-row-action">${removeButtonHtml()}</span>`
    : '';
  if (!available) {
    // Осиротевшая строка (Q3): «Партия недоступна» + аффорданс «Убрать» (AC-A8). Вне сетки
    // колонок (flex-override в book.css) — короткое сообщение + кнопка удаления.
    return `<div class="book-row book-row--unavailable data-table-row" role="button" tabindex="0" data-short-id="${escapeHtml(row.short_id)}" data-available="false">
      <span class="book-row-unavailable">${escapeHtml(t('book.row.unavailable'))}</span>
      ${album === 'others' ? removeButtonHtml() : ''}
    </div>`;
  }
  // Колонки (subgrid из data-table): Счёт | Ходы | Дата [| Действие у «Чужих»].
  return `<div class="book-row data-table-row" role="button" tabindex="0" data-short-id="${escapeHtml(row.short_id)}" data-available="true">
    <span class="book-row-score" aria-label="${escapeHtml(t('book.row.scoreAria'))}">${formatRuNumber(row.score_verified)}</span>
    <span class="book-row-moves" aria-label="${escapeHtml(t('book.row.movesAria'))}">${formatRuNumber(row.moves_count)}</span>
    <span class="book-row-end"><span class="book-row-date">${escapeHtml(dateLabel)}</span></span>
    ${actionCell}
  </div>`;
}

/** Сильный empty-state (AC-A9): CTA, НЕ «нет данных». Каждая вкладка самостоятельна.
 * `guestUser` — для «Чужих» переключает текст: гость не может сохранять чужие записи
 * (owner-directive 2026-06-25). */
function renderEmpty(album: BookAlbum, tab: TabId, lang: ContentLang, guestUser: boolean): string {
  if (album === 'others') {
    if (guestUser) {
      // Гость НЕ может сохранять чужие записи → объясняем + CTA логина (owner-directive
      // 2026-06-25). Текст → t('book.empty.othersGuest'); CTA-кнопка — общий book.loginCta.
      return `<div class="book-empty">
        <p class="book-empty-lead">${escapeHtml(t('book.empty.othersGuest'))}</p>
        <button type="button" class="book-login-cta btn primary">${escapeHtml(t('book.loginCta'))}</button>
      </div>`;
    }
    // Авторизованный: как добавлять чужие записи (дискета из лидерборда).
    return `<div class="book-empty">
      <p class="book-empty-lead">${escapeHtml(t('book.empty.others'))}</p>
      <button type="button" class="book-empty-cta btn primary">${escapeHtml(t('book.empty.othersCta'))}</button>
    </div>`;
  }
  if (tab === DAILY_TAB_ID) {
    return `<div class="book-empty">
      <p class="book-empty-lead">${escapeHtml(t('book.empty.ownDaily'))}</p>
      <button type="button" class="book-empty-cta btn primary">${escapeHtml(t('book.empty.ownDailyCta'))}</button>
    </div>`;
  }
  const entry = MODE_REGISTRY.find((e) => e.mode === tab);
  const name = entry ? modeLabel(entry, lang) : tab;
  return `<div class="book-empty">
    <p class="book-empty-lead">${escapeHtml(t('book.empty.own', { mode: name }))}</p>
    <button type="button" class="book-empty-cta btn primary">${escapeHtml(t('book.empty.ownCta', { mode: name }))}</button>
  </div>`;
}

// renderGuestCta (стена «Войди, чтобы открыть Книгу», book.login) УДАЛЕНА (owner-directive
// 2026-06-25): противоречила «гость видит свои реплеи» + плодила лишний текст. Гость теперь
// видит обычный empty-state (renderEmpty с guestUser=true).

/** Скелет холодного захода (instant-hydration §5) — структура таблицы (header + пустые
 * строки), zero layout-shift. Принимает album → header держит ту же колонку-действие. */
function renderSkeleton(album: BookAlbum): string {
  const n = 6;
  let rows = '';
  for (let i = 0; i < n; i++) rows += '<div class="book-row book-row--skeleton data-table-row"></div>';
  return renderTable(rows, album);
}
